import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [pendingMFA, setPendingMFA] = useState(null);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Handle 401 errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    
    if (response.data.requires_mfa) {
      setPendingMFA({
        sessionToken: response.data.session_token,
        message: response.data.message
      });
      return { requiresMFA: true };
    }

    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return { requiresMFA: false, user: response.data.user };
  };

  const verifyMFA = async (otpCode) => {
    if (!pendingMFA) throw new Error('No pending MFA session');

    const response = await api.post('/auth/verify-mfa', {
      session_token: pendingMFA.sessionToken,
      otp_code: otpCode
    });

    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    setPendingMFA(null);
    return response.data.user;
  };

  const resendOTP = async () => {
    if (!pendingMFA) throw new Error('No pending MFA session');
    await api.post(`/auth/resend-otp?session_token=${pendingMFA.sessionToken}`);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPendingMFA(null);
  };

  const setupAlternateEmail = async (alternateEmail) => {
    await api.post('/auth/setup-alternate-email', { alternate_email: alternateEmail });
  };

  const verifyAlternateEmail = async (otpCode) => {
    await api.post(`/auth/verify-alternate-email?otp_code=${otpCode}`);
    await fetchUser();
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  };

  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role === 'employee';
  const isPhysician = user?.role === 'physician';

  const value = {
    user,
    token,
    loading,
    pendingMFA,
    login,
    verifyMFA,
    resendOTP,
    logout,
    setupAlternateEmail,
    verifyAlternateEmail,
    changePassword,
    isAdmin,
    isEmployee,
    isPhysician,
    api,
    refreshUser: fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
